import { GetALLUsers } from '@/apis/onboarding';
import { fetchMentionUsers } from '@/apis/shipment';
import styles from './mentionList.module.css';

/** Where the @ list gets the people an author can mention. */
export enum UserMentionSource {
  /** The company's users, from `GetALLUsers`. */
  COMPANY_USERS = 'company_users',
  /**
   * The shipment comment box's list, from `fetchMentionUsers`: users, teams,
   * offices, office contacts, and the Controlling office sentinel in a
   * workflow with an alert trigger.
   */
  COMMENT_MENTIONABLES = 'comment_mentionables',
}

/** One person or group in the @ list. */
export interface MentionListUser {
  id: string;
  name: string;
  email: string;
  badgeColor: string | undefined;
}

/** One page of the @ list. */
export interface MentionListPage {
  users: MentionListUser[];
  pageCount: number;
}

/**
 * Fetches one page of the @ list from `source`, matching `search`.
 * `includeControllingOffice` adds the Controlling office sentinel, which only
 * `COMMENT_MENTIONABLES` offers.
 */
export async function fetchMentionListPage(
  source: UserMentionSource,
  token: string,
  page: number,
  search: string,
  includeControllingOffice: boolean
): Promise<MentionListPage> {
  switch (source) {
    case UserMentionSource.COMPANY_USERS: {
      const response = await GetALLUsers(token, false, page, search);
      const users = response?.data?.data?.data ?? [];
      return {
        users: users.map((user) => ({
          id: String(user.id || user.user_id || ''),
          name: user.name || user.email || user.username || 'Unknown',
          email: user.email || user.username || '',
          badgeColor: user.badge_color,
        })),
        pageCount: response?.data?.data?.page_count || 1,
      };
    }
    case UserMentionSource.COMMENT_MENTIONABLES: {
      const response = await fetchMentionUsers(token, undefined, page, search, {
        logiblocks: includeControllingOffice,
      });
      const users = response?.data?.data?.data ?? [];
      return {
        users: users.map((user) => ({
          id: String(user.id || ''),
          name: user.name || user.email || 'Unknown',
          email: user.email || '',
          badgeColor: user.badge_color,
        })),
        pageCount: response?.data?.data?.page_count || 1,
      };
    }
    default:
      console.error(`Unknown user mention source: ${source}`);
      return { users: [], pageCount: 1 };
  }
}

/**
 * Fills `container` with the @ list: the people `fetchPage` returns for the
 * query, a page at a time, with the next page loaded on scroll. Calls
 * `command` with the person clicked. Returns an `update` that takes a new
 * query.
 */
export function renderMentionListWithAPI(
  container: HTMLElement,
  command: (item: { id: string; label: string }) => void,
  fetchPage: (page: number, search: string) => Promise<MentionListPage>,
  initialQuery: string
) {
  let currentPage = 1;
  let hasMore = true;
  let isLoading = false;
  let allUsers: MentionListUser[] = [];
  let currentQuery = initialQuery;
  let activeCommand = command;

  container.classList.add(styles.list);

  const fetchUsers = async (page: number, search: string) => {
    if (page > 1 && (isLoading || !hasMore)) {
      return;
    }
    isLoading = true;
    if (page === 1) {
      showLoading();
    }

    try {
      const { users, pageCount } = await fetchPage(page, search);
      if (search !== currentQuery) {
        return;
      }

      if (users.length > 0) {
        allUsers = page === 1 ? users : [...allUsers, ...users];
        hasMore = page < pageCount;
        currentPage = page;
        renderUsers();
      } else {
        hasMore = false;
        if (allUsers.length === 0) {
          showNoResults();
        } else {
          renderUsers();
        }
      }
    } catch {
      if (search !== currentQuery) {
        return;
      }
      hasMore = false;
      if (allUsers.length === 0) {
        showError();
      } else {
        renderUsers();
      }
    } finally {
      if (search === currentQuery) {
        isLoading = false;
      }
    }
  };

  const renderUsers = () => {
    container.innerHTML = '';
    if (allUsers.length === 0 && !isLoading) {
      showNoResults();
      return;
    }

    allUsers.forEach((user) => {
      const userName = user.name;
      const userEmail = user.email;

      const option = document.createElement('div');
      option.className = styles.option;

      const avatar = document.createElement('div');
      avatar.className = styles.avatar;
      avatar.style.backgroundColor = user.badgeColor || '#1677ff';
      avatar.textContent = userName !== '' ? userName[0].toUpperCase() : '?';

      const textWrap = document.createElement('div');
      textWrap.className = styles.text;

      const name = document.createElement('div');
      name.className = styles.name;
      name.textContent = userName;

      textWrap.appendChild(name);

      if (userEmail !== '' && userEmail !== userName) {
        const email = document.createElement('div');
        email.className = styles.email;
        email.textContent = userEmail;
        textWrap.appendChild(email);
      }

      option.appendChild(avatar);
      option.appendChild(textWrap);
      option.onclick = () => activeCommand({ id: user.id, label: userName });
      container.appendChild(option);
    });

    if (hasMore) {
      const more = document.createElement('div');
      more.className = styles.more;
      more.textContent = isLoading ? 'Loading...' : 'Scroll for more';
      container.appendChild(more);
    }
  };

  const showStatus = (text: string, className: string) => {
    const status = document.createElement('div');
    status.className = className;
    status.textContent = text;
    container.replaceChildren(status);
  };
  const showLoading = () => showStatus('Loading...', styles.status);
  const showNoResults = () => showStatus('No users found', styles.status);
  const showError = () =>
    showStatus(
      'Failed to load users',
      `${styles.status} ${styles.statusError}`
    );

  container.onscroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (
      scrollTop + clientHeight >= scrollHeight - 10 &&
      hasMore &&
      !isLoading
    ) {
      fetchUsers(currentPage + 1, currentQuery);
    }
  };

  fetchUsers(1, initialQuery);

  return {
    update: (newQuery: string, newCommand?: typeof command) => {
      if (newCommand) {
        activeCommand = newCommand;
      }
      if (newQuery !== currentQuery) {
        currentQuery = newQuery;
        currentPage = 1;
        hasMore = true;
        allUsers = [];
        fetchUsers(1, newQuery);
      }
    },
  };
}
